import {
    List,
    Datagrid,
    TextField,
    NumberField,
    DateField,
    ChipField,
    useRecordContext,
    Show,
    SimpleShowLayout,
    TabbedShowLayout,
    Tab,
    ArrayField,
    SingleFieldList,
    useRefresh,
    useNotify,
    Button,
    TopToolbar,
    CreateButton,
    RefreshButton,
} from 'react-admin';
import { Chip, Box, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImportManager from '../components/ImportManager';

// Status field with color coding
const StatusField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const statusColors = {
        pending: 'warning',
        processing: 'info',
        completed: 'success',
        failed: 'error',
        cancelled: 'default'
    };

    return (
        <Chip 
            label={record.status}
            color={statusColors[record.status] || 'default'}
            size="small"
            variant="filled"
            sx={{ textTransform: 'capitalize' }}
        />
    );
};

// Progress field showing completion percentage
const ProgressField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const percentage = record.total_rows > 0 
        ? Math.round((record.processed_rows / record.total_rows) * 100)
        : 0;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
                {record.processed_rows} / {record.total_rows}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                ({percentage}%)
            </Typography>
        </Box>
    );
};

// Results summary field
const ResultsField = () => {
    const record = useRecordContext();
    if (!record) return null;

    if (record.status === 'pending' || record.status === 'processing') {
        return <Typography variant="body2" color="text.secondary">Processing...</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip 
                label={`Created: ${record.created_count}`} 
                color="success" 
                size="small" 
                variant="outlined"
            />
            <Chip 
                label={`Updated: ${record.updated_count}`} 
                color="info" 
                size="small" 
                variant="outlined"
            />
            <Chip 
                label={`Errors: ${record.error_count}`} 
                color="error" 
                size="small" 
                variant="outlined"
            />
        </Box>
    );
};

// Dry Run field showing if it was a test run
const DryRunField = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Chip 
            label={record.dry_run ? "Test Run" : "Live Import"}
            color={record.dry_run ? "warning" : "primary"}
            size="small"
            variant="filled"
            sx={{ 
                fontWeight: 'bold',
                '& .MuiChip-label': {
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                }
            }}
        />
    );
};

// Duration field
const DurationField = () => {
    const record = useRecordContext();
    if (!record) return null;

    if (!record.started_at) return <span>-</span>;

    const start = new Date(record.started_at);
    const end = record.completed_at ? new Date(record.completed_at) : new Date();
    const duration = Math.round((end - start) / 1000); // in seconds

    if (duration < 60) {
        return <span>{duration}s</span>;
    } else if (duration < 3600) {
        return <span>{Math.round(duration / 60)}m</span>;
    } else {
        return <span>{Math.round(duration / 3600)}h</span>;
    }
};

// Custom actions for the list
const ImportListActions = () => {
    console.log('ImportListActions rendering...');
    
    return (
        <TopToolbar>
            <RefreshButton />
            <Button
                label="New Import"
                startIcon={<UploadIcon />}
                onClick={() => {
                    console.log('New Import button clicked');
                    // This will be handled by the ImportManager component
                    const event = new CustomEvent('openImportDialog');
                    window.dispatchEvent(event);
                }}
                variant="contained"
                color="primary"
            />
            <Button
                label="Download Template"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                    console.log('Download Template button clicked');
                    try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/import/products/template`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                            },
                        });
                        
                        if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'products_import_template.xlsx';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                        } else {
                            console.error('Template download failed:', response.status);
                        }
                    } catch (error) {
                        console.error('Error downloading template:', error);
                    }
                }}
                variant="outlined"
            />
        </TopToolbar>
    );
};

export const ImportList = () => {
    console.log('ImportList component rendering...');
    
    return (
        <>
            {/* Always show these buttons at the top */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', mb: 2 }}>
                <Typography variant="h4" gutterBottom>
                    Product Import Jobs
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        label="New Import"
                        startIcon={<UploadIcon />}
                        onClick={() => {
                            console.log('New Import button clicked (fallback)');
                            const event = new CustomEvent('openImportDialog');
                            window.dispatchEvent(event);
                        }}
                        variant="contained"
                        color="primary"
                    />
                    <Button
                        label="Download Template"
                        startIcon={<DownloadIcon />}
                        onClick={async () => {
                            console.log('Download Template button clicked (fallback)');
                            try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/import/products/template`, {
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                    },
                                });
                                
                                if (response.ok) {
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = 'products_import_template.xlsx';
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                } else {
                                    console.error('Template download failed:', response.status);
                                    alert('Template download failed. Please check if the backend is running.');
                                }
                            } catch (error) {
                                console.error('Error downloading template:', error);
                                alert('Error downloading template: ' + error.message);
                            }
                        }}
                        variant="outlined"
                    />
                </Box>
            </Box>
            
            <List 
                sort={{ field: 'id', order: 'DESC' }}
                perPage={25}
                actions={<ImportListActions />}
                title="Import History"
                empty={
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No import jobs yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Import" above to start your first product import
                        </Typography>
                    </Box>
                }
            >
                <Datagrid
                    rowClick="show"
                    rowStyle={(record) => ({
                        backgroundColor: record.status === 'failed' ? '#ffebee' : 'inherit',
                    })}
                >
                    <TextField source="job_id" label="Job ID" />
                    <TextField source="file_name" label="File Name" />
                    <StatusField label="Status" />
                    <DryRunField label="Type" />
                    <ProgressField label="Progress" />
                    <ResultsField label="Results" />
                    <DurationField label="Duration" />
                    <DateField source="started_at" label="Started" showTime />
                    <DateField source="completed_at" label="Completed" showTime />
                </Datagrid>
            </List>
            <ImportManager />
        </>
    );
};

// Error details component
const ErrorDetails = () => {
    const record = useRecordContext();
    if (!record || !record.errors || record.errors.length === 0) {
        return <Typography>No errors</Typography>;
    }

    return (
        <Box>
            {record.errors.map((error, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="error">
                        Row {error.row_number} - {error.field}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {error.error_message}
                    </Typography>
                    {error.data && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Data: {error.data}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export const ImportShow = () => (
    <Show title="Import Job Details">
        <TabbedShowLayout>
            <Tab label="Overview">
                <SimpleShowLayout>
                    <TextField source="job_id" label="Job ID" />
                    <TextField source="file_name" label="File Name" />
                    <StatusField label="Status" />
                    <DryRunField label="Import Type" />
                    <NumberField source="total_rows" label="Total Rows" />
                    <NumberField source="processed_rows" label="Processed Rows" />
                    <ProgressField label="Progress" />
                    <NumberField source="created_count" label="Products Created" />
                    <NumberField source="updated_count" label="Products Updated" />
                    <NumberField source="error_count" label="Errors" />
                    <DateField source="started_at" label="Started At" showTime />
                    <DateField source="completed_at" label="Completed At" showTime />
                    <DurationField label="Duration" />
                </SimpleShowLayout>
            </Tab>
            <Tab label="Errors" badge={record => record.error_count}>
                <ErrorDetails />
            </Tab>
        </TabbedShowLayout>
    </Show>
);

// We don't need Create or Edit for import jobs as they're created via file upload
export const ImportCreate = null;
export const ImportEdit = null;
